import { createReducer } from "@reduxjs/toolkit";
import {
  initObjectives,
  completeProtocol,
  completeStep,
  completeAndSkipStep,
  incrementStep,
  incrementCurrentStepSheetChanges,
  incrementUnsanitisedSheetChanges,
} from "../actions";

/**
 * Initial state for the stepReducer.
 */
const initialState = {
  scenes: [], // Array of scenes, each containing objectives, step groups, and steps
};

/**
 * Reducer for managing steps and their states in objectives and scenes.
 */
const stepReducer = createReducer(initialState, (builder) => {
  /**
   * Initialize objectives for each scene.
   * Sets up the hierarchical structure of scenes, objectives, step groups, and steps.
   */
  builder.addCase(initObjectives, (state, action) => {
    const scenes = action.payload.map((scene) => ({
      ...scene,
      complete: false,
      currentObjectiveIndex: 0,
      currentStepGroupIndex: 0,
      currentStepIndex: 0,
      objectives: scene?.objectives?.map((objective) => ({
        ...objective,
        stepGroups: objective.stepGroups.map((stepGroup) => ({
          ...stepGroup,
          steps: stepGroup.steps.map((step) => ({
            ...step,
            complete: false,
            skipped: false,
            sheetChanges: 0,
            unsanitisedSheetChanges: 0,
          })),
        })),
      })),
    }));
    state.scenes = scenes;
  });

  /**
   * Mark a protocol (scene) as complete if all its objectives, step groups, and steps are complete.
   */
  builder.addCase(completeProtocol, (state, action) => {
    const { sceneId } = action.payload;
    const scene = state.scenes.find((scene) => scene.id === sceneId);
    if (!scene) return;

    scene.complete = scene.objectives.every((objective) =>
      objective.stepGroups.every((stepGroup) =>
        stepGroup.steps.every((step) => step.complete)
      )
    );
  });

  /**
   * Mark a specific step as complete.
   */
  builder.addCase(completeStep, (state, action) => {
    const { sceneId, objectiveIndex, stepGroupIndex, stepIndex } =
      action.payload;
    const scene = state.scenes.find((scene) => scene.id === sceneId);
    if (!scene) return;

    const step =
      scene.objectives?.[objectiveIndex]?.stepGroups?.[stepGroupIndex]?.steps?.[
        stepIndex
      ];
    if (step) {
      step.complete = true;
    }
  });

  /**
   * Mark a specific step as complete and skipped.
   */
  builder.addCase(completeAndSkipStep, (state, action) => {
    const { sceneId, objectiveIndex, stepGroupIndex, stepIndex } =
      action.payload;
    const scene = state.scenes.find((scene) => scene.id === sceneId);
    if (!scene) return;

    const step =
      scene.objectives?.[objectiveIndex]?.stepGroups?.[stepGroupIndex]?.steps?.[
        stepIndex
      ];
    if (step) {
      step.complete = true;
      step.skipped = true;
    }
  });

  /**
   * Increment to the next step within the current scene and objective hierarchy.
   * If the last step in the current group is reached, moves to the next group or objective.
   */
  builder.addCase(incrementStep, (state, action) => {
    const { sceneId } = action.payload;
    const scene = state.scenes.find((scene) => scene.id === sceneId);
    if (!scene) return;

    const objective = scene.objectives[scene.currentObjectiveIndex];
    const stepGroup = objective.stepGroups[scene.currentStepGroupIndex];

    const nextStepIndex = scene.currentStepIndex + 1;

    if (nextStepIndex >= stepGroup.steps.length) {
      const nextStepGroupIndex = scene.currentStepGroupIndex + 1;
      if (nextStepGroupIndex >= objective.stepGroups.length) {
        const nextObjectiveIndex = scene.currentObjectiveIndex + 1;
        if (nextObjectiveIndex < scene.objectives.length) {
          // Move to the next objective
          scene.currentObjectiveIndex = nextObjectiveIndex;
          scene.currentStepGroupIndex = 0;
          scene.currentStepIndex = 0;
        }
      } else {
        // Move to the next step group
        scene.currentStepGroupIndex = nextStepGroupIndex;
        scene.currentStepIndex = 0;
      }
    } else {
      // Move to the next step
      scene.currentStepIndex = nextStepIndex;
    }
  });

  /**
   * Increment the `sheetChanges` counter for a specific step.
   */
  builder.addCase(incrementCurrentStepSheetChanges, (state, action) => {
    const { sceneId, objectiveIndex, stepGroupIndex, stepIndex } =
      action.payload;
    const scene = state.scenes.find((scene) => scene.id === sceneId);
    if (!scene) return;

    const step =
      scene.objectives?.[objectiveIndex]?.stepGroups?.[stepGroupIndex]?.steps?.[
        stepIndex
      ];
    if (step) {
      step.sheetChanges += 1;
    }
  });

  /**
   * Increment the `unsanitisedSheetChanges` counter for a specific step.
   */
  builder.addCase(incrementUnsanitisedSheetChanges, (state, action) => {
    const { sceneId, objectiveIndex, stepGroupIndex, stepIndex } =
      action.payload;
    const scene = state.scenes.find((scene) => scene.id === sceneId);
    if (!scene) return;

    const step =
      scene.objectives?.[objectiveIndex]?.stepGroups?.[stepGroupIndex]?.steps?.[
        stepIndex
      ];
    if (step) {
      step.unsanitisedSheetChanges += 1;
    }
  });
});

export default stepReducer;
